/**
 * FileDrop (U5): drag-and-drop + click-to-browse upload target.
 * Storage/validation live upstream (FilesModule, U11) — this is the surface.
 */
import { useRef, useState, type JSX, type ReactNode } from 'react';
import { cx } from './cx';
import { IconUpload } from './icons';

export interface FileDropProps {
  onFiles: (files: File[]) => void;
  /** Native accept string, e.g. ".csv,text/csv" or "image/*". */
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  label?: ReactNode;
  hint?: ReactNode;
  compact?: boolean;
  className?: string;
}

export function FileDrop({
  onFiles,
  accept,
  multiple = false,
  disabled = false,
  label,
  hint,
  compact = false,
  className,
}: FileDropProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [active, setActive] = useState(false);

  function emit(list: FileList | null): void {
    if (!list || list.length === 0) return;
    const files = Array.from(list);
    onFiles(multiple ? files : files.slice(0, 1));
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      className={cx(
        'bl-filedrop',
        compact && 'bl-filedrop--compact',
        active && 'is-active',
        disabled && 'is-disabled',
        className,
      )}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(event) => {
        if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragEnter={(event) => {
        event.preventDefault();
        if (disabled) return;
        dragDepth.current += 1;
        setActive(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => {
        event.preventDefault();
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setActive(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        dragDepth.current = 0;
        setActive(false);
        if (!disabled) emit(event.dataTransfer.files);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        className="bl-visually-hidden"
        tabIndex={-1}
        onChange={(event) => {
          emit(event.currentTarget.files);
          event.currentTarget.value = '';
        }}
      />
      <span className="bl-filedrop__icon" aria-hidden="true">
        <IconUpload size={18} />
      </span>
      <span className="bl-filedrop__label">
        {label ?? (
          <>
            Drop files here or <em>browse</em>
          </>
        )}
      </span>
      {hint != null && <span className="bl-filedrop__hint">{hint}</span>}
    </div>
  );
}
